import React from "react";
import { Icon } from "@material-ui/core";

import { getStaticUrl } from "js/config";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import InfoToolTip from "js/react/components/InfoToolTip";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { ImageStyles, AiImageStyles, AiImageStyleTypes } from "common/aiConstants";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { ChooseThemeDialog } from "js/editor/ThemeEditor/ChooseThemeDialog";
import WorkspaceController from "js/controllers/WorkspaceController";
import { ActionPermissionsObject } from "common/interfaces";

import CustomImageStyleDialog from "../dialogs/CustomImageStyleDialog";
import { ImageOptionsGrid } from "./PaneComponents";

export function ImageStyleOption(props: {
    imageStyle: ImageStyles,
    selectedImageStyle?: ImageStyles,
    iconOnly?: boolean,
    labelOnly?: boolean,
    width?: number,
    height?: number,
    onClick?: (imageStyle: ImageStyles) => void,
}) {
    const {
        imageStyle,
        selectedImageStyle,
        iconOnly = false,
        labelOnly = false,
        width = 96,
        height = 72,
        onClick = () => { },
    } = props;

    let iconSource = null;
    let labelMessage = null;
    if (imageStyle === "theme") {
        iconSource = <Icon color="disabled">palette</Icon>;
        labelMessage = <TextContent uppercase center small>Theme Style</TextContent>;
    } else if (imageStyle === "custom") {
        iconSource = <Icon color="disabled">edit</Icon>;
        labelMessage = <TextContent uppercase center small>Custom Style</TextContent>;
    } else if (imageStyle === "none") {
        iconSource = <Icon color="disabled">not_interested</Icon>;
        labelMessage = <TextContent uppercase center small>None</TextContent>;
    } else if (imageStyle === "stock") {
        iconSource = <img src={getStaticUrl("/images/ai-image-styles/stock.svg")} width={width} height={height} />;
    } else {
        iconSource = <img src={getStaticUrl(`/images/ai-image-styles/${AiImageStyles[imageStyle].icon}`)} width={width} height={height} />;
        labelMessage = <TextContent uppercase center small>{AiImageStyles[imageStyle].label}</TextContent>;
    }

    const selected = selectedImageStyle === imageStyle;
    const handleClick = () => onClick && onClick(imageStyle);

    const iconElement = (
        <FlexLayout justify="center" align="center" selected={selected}
            width={width + 2} height={height + 2}
            border={["theme", "custom", "none"].includes(imageStyle) ? "1px solid #22222226" : ""}
            clickable onClick={handleClick}
        >
            {iconSource}
        </FlexLayout>
    );

    const labelElement = (
        <FlexLayout justify="center">
            {labelMessage}
        </FlexLayout>
    );

    return iconOnly ? iconElement : labelOnly ? labelMessage : (
        <FlexLayout gap={10} direction="column" width="fit-content">
            {iconElement}
            {labelElement}
        </FlexLayout>
    );
}

export function ImageStyleMenu(props: {
    allowedImageStyles: ImageStyles[];
    selectedImageStyle: ImageStyles;
    onImageStyleSelected: (imageStyle: ImageStyles) => void;
}) {
    const { allowedImageStyles, selectedImageStyle, onImageStyleSelected } = props;

    return (
        <ImageOptionsGrid>
            {allowedImageStyles.map(style => (
                <ImageStyleOption key={style}
                    imageStyle={style}
                    selectedImageStyle={selectedImageStyle}
                    onClick={onImageStyleSelected} />
            ))}
        </ImageOptionsGrid>
    );
}

export default function ImageStyleInput(props: {
    theme: any,
    onThemeChange: (theme: any) => void,
    imageStyle: ImageStyles,
    imageStylePrompt: string,
    onImageStyleChange: (imageStyle: ImageStyles, imageStylePrompt: string) => void
}) {
    const { theme, onThemeChange, imageStyle, imageStylePrompt, onImageStyleChange } = props;

    const handleImageStyleChange = (imageStyle: ImageStyles) => {
        if (imageStyle === "custom") {
            openCustomImageStyleDialog();
        } else {
            const imageStylePrompt = imageStyle === "theme" ? theme.get("imageStylePrompt")
                : imageStyle === "stock" ? "" : AiImageStyles[imageStyle].prompt;
            onImageStyleChange(imageStyle, imageStylePrompt);
        }
    };

    const openChooseThemeDialog = () => {
        const onlyTeamThemes = !WorkspaceController.actionPermissions[ActionPermissionsObject.CUSTOM_THEMES].use;

        return ShowDialog(ChooseThemeDialog, {
            title: "Switch Theme",
            selectedThemeId: theme.id,
            showEditCurrentTheme: false,
            onlyTeamThemes,
            hideBuiltInThemes: onlyTeamThemes,
            hideUserThemes: onlyTeamThemes,
            callback: newTheme => {
                onThemeChange(newTheme);
            }
        });
    };

    const openCustomImageStyleDialog = () => {
        return ShowDialog(CustomImageStyleDialog, {
            theme,
            imageStyle,
            imageStylePrompt,
            onSuccess: (imageStyle: ImageStyles, imageStylePrompt: string) => {
                onImageStyleChange(imageStyle, imageStylePrompt);
            }
        });
    };

    return (
        <FlexLayout gap={100}>
            {theme && (
                <FlexLayout gap={10} direction="column" minWidth={225}>
                    <TextContent description>Theme</TextContent>
                    <ThemeThumbnail item={theme} onClick={openChooseThemeDialog} />
                </FlexLayout>
            )}

            {WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use && WorkspaceController.experiments.ai_gen_images && (
                <FlexLayout direction="column" gap={15}>
                    <FlexLayout gap={10}>
                        <TextContent description>Image Source and Style</TextContent>
                        <InfoToolTip
                            title="Select a style for images generated with AI, or create your own prompt." />
                    </FlexLayout>
                    <FlexLayout gap={60}>
                        <FlexLayout gap={15} direction="column" width="fit-content" minWidth={100}>
                            <TextContent uppercase>Stock + Web</TextContent>
                            <ImageStyleOption imageStyle="stock" selectedImageStyle={imageStyle} onClick={handleImageStyleChange} />
                        </FlexLayout>
                        <FlexLayout gap={15} direction="column">
                            <TextContent uppercase>Generate with AI</TextContent>
                            <FlexLayout gap={20} wrap="wrap">
                                {["theme", ...Object.values(AiImageStyleTypes), "custom"].map((style: ImageStyles) => (
                                    <ImageStyleOption key={style} imageStyle={style} selectedImageStyle={imageStyle} onClick={handleImageStyleChange} />
                                ))}
                            </FlexLayout>
                        </FlexLayout>
                    </FlexLayout>
                </FlexLayout>
            )}
        </FlexLayout>
    );
}
